import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Separator } from '@/components/ui/shadcn/separator';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import {
    buildModules,
    FILTERS,
    getPermissionCategory,
    getPermissionDescription,
    getRoleDescription,
    MODULE_ICONS,
    type PermissionCategory,
    prettyActionLabel,
    RISKY_PERMISSIONS,
} from '@/lib/permissions/permissionModules';
import { cn } from '@/lib/utils';
import { useForm } from '@inertiajs/react';
import { AlertTriangle, CheckCircle2, Clock3, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

type Props = {
    userId: string;
};

type AclPayload = {
    user?: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
    roles?: string[];
    direct_permissions?: string[];
    denied_permissions?: string[];
    effective_permissions?: string[];
    available_roles?: string[];
    available_permissions?: string[];
    roles_permissions?: Record<string, string[]>;
};

export default function PermissionsTab({ userId }: Props) {
    const { t } = useTranslations();
    const [loading, setLoading] = useState(true);
    const [payloadUser, setPayloadUser] = useState<AclPayload['user']>();
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
    const [rolesPermissions, setRolesPermissions] = useState<Record<string, string[]>>({});
    const [permissionSearch, setPermissionSearch] = useState('');
    const [roleSearch, setRoleSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState<PermissionCategory>('all');
    const [savedState, setSavedState] = useState<{ roles: string[]; permissions: string[] }>({
        roles: [],
        permissions: [],
    });

    const { data, setData, put, processing } = useForm<{ roles: string[]; permissions: string[] }>({
        roles: [],
        permissions: [],
    });

    useEffect(() => {
        let mounted = true;

        fetch(`/users/${userId}/acl`, { headers: { Accept: 'application/json' } })
            .then((response) => response.json())
            .then((payload: AclPayload) => {
                if (!mounted) {
                    return;
                }

                const nextRoles = (payload.roles ?? []) as string[];
                const effectivePermissions = (payload.effective_permissions ?? []) as string[];

                setPayloadUser(payload.user);
                setAvailableRoles((payload.available_roles ?? []) as string[]);
                setAvailablePermissions((payload.available_permissions ?? []) as string[]);
                setRolesPermissions((payload.roles_permissions ?? {}) as Record<string, string[]>);

                setData({ roles: nextRoles, permissions: effectivePermissions });
                setSavedState({ roles: nextRoles, permissions: effectivePermissions });
                setLoading(false);
            })
            .catch(() => setLoading(false));

        return () => {
            mounted = false;
        };
    }, [userId, setData]);

    const permissionModules = useMemo(() => buildModules(availablePermissions), [availablePermissions]);

    const roleFilteredList = useMemo(() => {
        const q = roleSearch.trim().toLowerCase();

        if (!q) {
            return availableRoles;
        }

        return availableRoles.filter((role) => role.toLowerCase().includes(q));
    }, [availableRoles, roleSearch]);

    const filteredModules = useMemo(() => {
        const q = permissionSearch.trim().toLowerCase();

        return permissionModules
            .map((module) => {
                const permissions = module.permissions.filter((permission) => {
                    const matchesQuery = !q || permission.toLowerCase().includes(q) || prettyActionLabel(permission).toLowerCase().includes(q);

                    if (!matchesQuery) {
                        return false;
                    }

                    if (activeFilter === 'all') {
                        return true;
                    }

                    if (activeFilter === 'risky') {
                        return RISKY_PERMISSIONS.has(permission) || permission.endsWith('.delete');
                    }

                    return getPermissionCategory(permission) === activeFilter;
                });

                return { ...module, permissions };
            })
            .filter((module) => module.permissions.length > 0);
    }, [permissionModules, permissionSearch, activeFilter]);

    const selectedRole = data.roles[0] ?? '';

    const selectedRolePermissionSet = useMemo(() => {
        return new Set((selectedRole ? rolesPermissions[selectedRole] : []) ?? []);
    }, [rolesPermissions, selectedRole]);

    const changes = useMemo(() => {
        const before = new Set(savedState.permissions);
        const after = new Set(data.permissions);
        const added = [...after].filter((permission) => !before.has(permission));
        const removed = [...before].filter((permission) => !after.has(permission));

        return { added, removed };
    }, [savedState.permissions, data.permissions]);

    const hasUnsavedChanges = useMemo(() => {
        const sameRoles = JSON.stringify(savedState.roles) === JSON.stringify(data.roles);
        const samePermissions = JSON.stringify([...savedState.permissions].sort()) === JSON.stringify([...data.permissions].sort());

        return !(sameRoles && samePermissions);
    }, [savedState.permissions, savedState.roles, data.permissions, data.roles]);

    const enabledCount = data.permissions.length;
    const totalCount = availablePermissions.length;
    const sensitiveCount = data.permissions.filter((permission) => RISKY_PERMISSIONS.has(permission) || permission.endsWith('.delete')).length;

    const accessLevel = (() => {
        if (!totalCount) {
            return 'Low';
        }

        const ratio = enabledCount / totalCount;

        if (ratio > 0.8) return 'High';
        if (ratio > 0.5) return 'Medium-high';
        if (ratio > 0.3) return 'Medium';

        return 'Low';
    })();

    const handleRoleChange = (role: string): void => {
        const rolePermissions = rolesPermissions[role] ?? [];
        const currentRolePermissions = new Set((selectedRole ? rolesPermissions[selectedRole] : []) ?? []);
        const preservedCustomPermissions = data.permissions.filter((permission) => !currentRolePermissions.has(permission));
        const nextPermissions = [...new Set([...rolePermissions, ...preservedCustomPermissions])];

        setData('roles', role ? [role] : []);
        setData('permissions', nextPermissions);
    };

    const togglePermission = (permission: string, enabled: boolean): void => {
        if (enabled) {
            if (!data.permissions.includes(permission)) {
                setData('permissions', [...data.permissions, permission]);
            }

            return;
        }

        setData(
            'permissions',
            data.permissions.filter((entry) => entry !== permission),
        );
    };

    const save = (): void => {
        put(`/users/${userId}/acl`, {
            preserveScroll: true,
            onSuccess: () => {
                setSavedState({
                    roles: data.roles,
                    permissions: data.permissions,
                });
                toast.success(t('toasts.components_permissionstab.permissions_updated', 'Permissions updated'));
            },
            onError: () => toast.error(t('toasts.components_permissionstab.update_failed', 'Failed to update permissions')),
        });
    };

    const resetChanges = (): void => {
        setData({
            roles: savedState.roles,
            permissions: savedState.permissions,
        });
    };

    if (loading) {
        return <div className="text-muted-foreground p-4 text-sm">Loading permissions...</div>;
    }

    return (
        <div className="space-y-4">
            <Card className="gap-4">
                <CardHeader className="pb-0">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-1">
                            <CardTitle className="text-2xl">Role & Permissions</CardTitle>
                            <p className="text-muted-foreground text-sm">Manage what this user can view, create, edit, approve, or delete.</p>
                            <div className="text-muted-foreground flex flex-wrap items-center gap-4 pt-2 text-xs">
                                <span>User: {payloadUser ? `${payloadUser.first_name} ${payloadUser.last_name}` : userId}</span>
                                <span>Current role: {selectedRole || 'No role selected'}</span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock3 className="h-3.5 w-3.5" />
                                    Last updated in current session
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" onClick={resetChanges} disabled={!hasUnsavedChanges || processing}>
                                Reset changes
                            </Button>
                            <Button type="button" onClick={save} disabled={processing || !hasUnsavedChanges}>
                                {processing ? 'Saving...' : 'Save permissions'}
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                <CardContent>
                    <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)_320px]">
                        <Card className="gap-3 py-4">
                            <CardHeader className="px-4 pb-0">
                                <CardTitle className="text-base">Roles</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 px-4">
                                <div className="relative">
                                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                    <Input
                                        value={roleSearch}
                                        onChange={(event) => setRoleSearch(event.target.value)}
                                        placeholder="Search role..."
                                        className="pl-9"
                                    />
                                </div>

                                <div className="space-y-2">
                                    {roleFilteredList.map((role) => {
                                        const isSelected = selectedRole === role;
                                        const rolePermissionCount = (rolesPermissions[role] ?? []).length;

                                        return (
                                            <button
                                                key={role}
                                                type="button"
                                                onClick={() => handleRoleChange(role)}
                                                className={cn(
                                                    'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                                                    isSelected
                                                        ? 'border-primary bg-primary/5 ring-primary/30 ring-1'
                                                        : 'hover:bg-muted/40 border-border',
                                                )}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="text-sm font-semibold">{role}</p>
                                                        <p className="text-muted-foreground text-xs">{getRoleDescription(role)}</p>
                                                        <p className="text-muted-foreground mt-1 text-xs">{rolePermissionCount} permissions</p>
                                                    </div>
                                                    {isSelected && <CheckCircle2 className="text-primary mt-0.5 h-4 w-4" />}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="gap-3 py-4">
                            <CardHeader className="space-y-3 px-4 pb-0">
                                <CardTitle className="text-base">Permissions</CardTitle>
                                <div className="relative">
                                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                    <Input
                                        value={permissionSearch}
                                        onChange={(event) => setPermissionSearch(event.target.value)}
                                        placeholder="Search permissions..."
                                        className="pl-9"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {FILTERS.map((filter) => (
                                        <button
                                            key={filter.id}
                                            type="button"
                                            className={cn(
                                                'rounded-full border px-3 py-1 text-xs transition-colors',
                                                activeFilter === filter.id
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'text-muted-foreground hover:bg-muted/60',
                                            )}
                                            onClick={() => setActiveFilter(filter.id)}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </CardHeader>

                            <CardContent className="px-4">
                                <div className="space-y-3">
                                    {filteredModules.map((module) => {
                                        const Icon = MODULE_ICONS[module.id] ?? ShieldCheck;
                                        const enabledInModule = module.permissions.filter((permission) =>
                                            data.permissions.includes(permission),
                                        ).length;

                                        return (
                                            <div key={module.id} className="rounded-xl border p-3">
                                                <div className="flex items-start justify-between gap-3 pb-3">
                                                    <div className="flex items-start gap-3">
                                                        <div className="bg-primary/10 text-primary mt-0.5 rounded-full p-2">
                                                            <Icon className="h-4 w-4" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold">{module.label}</p>
                                                            <p className="text-muted-foreground text-xs">{module.description}</p>
                                                            <p className="text-primary mt-1 text-xs font-medium">
                                                                {enabledInModule} / {module.permissions.length} enabled
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                                    {module.permissions.map((permission) => {
                                                        const checked = data.permissions.includes(permission);
                                                        const isDefaultByRole = selectedRolePermissionSet.has(permission);
                                                        const isRisky = RISKY_PERMISSIONS.has(permission) || permission.endsWith('.delete');

                                                        return (
                                                            <div
                                                                key={permission}
                                                                className={cn(
                                                                    'flex items-center justify-between rounded-lg border px-2.5 py-2',
                                                                    checked ? 'border-primary/30 bg-primary/5' : 'border-border',
                                                                )}
                                                            >
                                                                <div className="min-w-0 pr-2">
                                                                    <p className="truncate text-xs font-medium">{prettyActionLabel(permission)}</p>
                                                                    {getPermissionDescription(permission) && (
                                                                        <p className="text-muted-foreground mt-0.5 line-clamp-2 text-[10px] leading-tight">
                                                                            {getPermissionDescription(permission)}
                                                                        </p>
                                                                    )}
                                                                    <div className="mt-1 flex items-center gap-1">
                                                                        {isDefaultByRole && (
                                                                            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                                                                Role
                                                                            </Badge>
                                                                        )}
                                                                        {isRisky && (
                                                                            <Badge
                                                                                variant="outline"
                                                                                className="text-warning-subtle-foreground px-1.5 py-0 text-[10px]"
                                                                            >
                                                                                Risky
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <Switch
                                                                    checked={checked}
                                                                    onCheckedChange={(next) => togglePermission(permission, Boolean(next))}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {!filteredModules.length && (
                                        <div className="text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
                                            No permissions match the current filters.
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="gap-3 py-4">
                            <CardHeader className="px-4 pb-0">
                                <CardTitle className="text-base">Permission summary</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4 px-4">
                                <div className="flex items-center gap-3 rounded-lg border p-3">
                                    <div className="bg-primary/10 text-primary rounded-full p-2">
                                        <ShieldCheck className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold">{selectedRole || 'No role selected'}</p>
                                        <p className="text-muted-foreground text-xs">Selected role</p>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Access level</span>
                                        <span>{accessLevel}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Enabled permissions</span>
                                        <span>
                                            {enabledCount} / {totalCount}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Sensitive permissions</span>
                                        <span>{sensitiveCount}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Changed in this session</span>
                                        <span>{changes.added.length + changes.removed.length}</span>
                                    </div>
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                    <p className="text-sm font-semibold">Changes</p>
                                    {!changes.added.length && !changes.removed.length && (
                                        <p className="text-muted-foreground text-xs">No pending changes.</p>
                                    )}
                                    {changes.added.slice(0, 5).map((permission) => (
                                        <div key={`added-${permission}`} className="text-success-subtle-foreground text-xs">
                                            + {permission}
                                        </div>
                                    ))}
                                    {changes.removed.slice(0, 5).map((permission) => (
                                        <div key={`removed-${permission}`} className="text-destructive text-xs">
                                            - {permission}
                                        </div>
                                    ))}
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                    <p className="text-sm font-semibold">Warnings</p>
                                    {data.permissions.some((permission) => permission.startsWith('work-orders.assign')) && (
                                        <div className="border-warning bg-warning-subtle text-warning-subtle-foreground flex items-start gap-2 rounded-md border p-2 text-xs">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                                            Can assign work orders to other users.
                                        </div>
                                    )}
                                    {data.permissions.includes('acl.manage') && (
                                        <div className="border-warning bg-warning-subtle text-warning-subtle-foreground flex items-start gap-2 rounded-md border p-2 text-xs">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                                            Can manage role and permission access.
                                        </div>
                                    )}
                                    {data.permissions.includes('settings.manage') && (
                                        <div className="border-warning bg-warning-subtle text-warning-subtle-foreground flex items-start gap-2 rounded-md border p-2 text-xs">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                                            Can change global system settings.
                                        </div>
                                    )}
                                </div>

                                <Button type="button" className="w-full" onClick={save} disabled={processing || !hasUnsavedChanges}>
                                    {processing ? 'Saving...' : 'Save permissions'}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
